import React from 'react';
import {
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Tooltip,
  Badge,
  alpha,
  useTheme,
  Divider,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Share as ShareIcon,
  Folder as FolderIcon,
  FolderOutlined as FolderOutlinedIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  DriveFileMove as MoveIcon,
  Edit as RenameIcon,
  InfoOutlined as InfoIcon,
  FileCopy as CopyIcon,
  LockOutlined as LockIcon,
  Apps as AppsIcon,
  Download as DownloadIcon,
  FolderZip as FolderZipIcon,
  RestoreFromTrash as RestoreFromTrashIcon,
} from '@mui/icons-material';
import { formatDate } from '../../utils/formatters';
import { Folder as FolderType } from '../../types';

interface FolderCardProps {
  folder: FolderType;
  onClick: () => void;
  onOperation: (operation: string, folderId: string, data?: any) => void;
  isSelected?: boolean;
  onSelectionChange?: (selected: boolean) => void;
  listVariant?: 'default' | 'trash';
}

const FolderCard: React.FC<FolderCardProps> = ({
  folder,
  onClick,
  onOperation,
  isSelected = false,
  onSelectionChange,
  listVariant = 'default',
}) => {
  const theme = useTheme();
  const isTrash = listVariant === 'trash';
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(menuAnchorEl);

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onSelectionChange) {
      onSelectionChange(!isSelected);
    }
  };

  // Handler for context menu icon click and right-click
  const handleMenuClick = (e: React.MouseEvent<HTMLElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleMenuItemClick = (operation: string, data?: any) => {
    handleMenuClose();
    onOperation(operation, folder.id, data);
  };
  
  // Create drag event handlers
  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    if (isTrash) return;
    e.dataTransfer.setData('application/json', JSON.stringify({
      id: folder.id,
      type: 'folder',
      name: folder.name
    }));
    e.dataTransfer.effectAllowed = 'move';
  };
  
  // Format date
  const dateFormatted = formatDate(folder.updatedAt || '');

  // Get folder color based on folder type or name
  const getFolderColor = () => {
    if (folder.isStarred) return theme.palette.warning.main;
    if (folder.isShared) return theme.palette.info.main;
    
    // Simple hash function to get consistent colors based on folder name
    let hash = 0;
    for (let i = 0; i < folder.name.length; i++) {
      hash = folder.name.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    // Use predefined colors for better visual consistency
    const colors = [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.success.main,
      theme.palette.info.main,
      '#9c27b0', // purple
      '#00bcd4', // cyan
      '#ff9800', // orange
    ];
    
    return colors[Math.abs(hash) % colors.length];
  };

  return (
    <Card
      draggable={!isTrash}
      onDragStart={isTrash ? undefined : handleDragStart}
      onContextMenu={handleMenuClick}
      elevation={0}
      sx={{
        position: 'relative',
        borderRadius: 2,
        overflow: 'visible',
        transition: 'all 0.2s ease-in-out',
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: isSelected 
          ? alpha(theme.palette.primary.main, 0.08)
          : 'background.paper',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[6],
        },
      }}
    >
      {onSelectionChange && (
        <Checkbox
          checked={isSelected}
          onClick={handleCheckboxClick}
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            zIndex: 2,
            backgroundColor: alpha(theme.palette.background.paper, 0.7),
            borderRadius: '50%',
            opacity: isSelected ? 1 : 0,
            '&:hover': {
              opacity: 1,
            },
            transition: 'opacity 0.2s ease',
          }}
        />
      )}
      
      <Box
        className="folder-actions"
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 2,
          display: 'flex',
          gap: 0.5,
          opacity: 1,
          transition: 'opacity 0.2s ease',
        }}
      >
        {!isTrash && (
        <Tooltip title={folder.isStarred ? 'Remove from favorites' : 'Add to favorites'}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onOperation('star', folder.id, !folder.isStarred);
            }}
            sx={{
              backgroundColor: alpha(theme.palette.background.paper, 0.7),
              '&:hover': {
                backgroundColor: alpha(theme.palette.background.paper, 0.9),
              },
            }}
          >
            {folder.isStarred ? (
              <StarIcon fontSize="small" color="primary" />
            ) : (
              <StarBorderIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
        )}
        
        <Tooltip title="More actions">
          <IconButton
            size="small"
            onClick={handleMenuClick}
            sx={{
              backgroundColor: alpha(theme.palette.background.paper, 0.7),
              '&:hover': {
                backgroundColor: alpha(theme.palette.background.paper, 0.9),
              },
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      
      <CardActionArea onClick={onClick}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            pt: 4,
            pb: 2,
            position: 'relative',
          }}
        >
          <Badge
            badgeContent={folder.isShared ? <ShareIcon fontSize="small" /> : undefined}
            color="primary"
            invisible={!folder.isShared}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            sx={{
              '& .MuiBadge-badge': {
                transform: 'scale(0.8) translate(40%, 40%)',
              },
            }}
          >
            <FolderIcon
              sx={{
                fontSize: 72,
                color: getFolderColor(),
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                },
              }}
            />
          </Badge>
          
          {folder.isProtected && (
            <LockIcon 
              sx={{ 
                position: 'absolute', 
                right: 'calc(50% - 40px)', 
                bottom: 20, 
                fontSize: 16,
                color: theme.palette.text.secondary
              }}
            />
          )}
        </Box>
        
        <CardContent sx={{ p: 2, pt: 0, pb: '16px !important' }}>
          <Tooltip title={folder.name}>
            <Typography 
              variant="subtitle2" 
              noWrap 
              sx={{ 
                textAlign: 'center',
                fontWeight: 500,
                fontSize: '0.875rem'
              }}
            >
              {folder.name}
            </Typography>
          </Tooltip>
          
          <Typography 
            variant="caption" 
            color="text.secondary" 
            component="div"
            sx={{ 
              textAlign: 'center',
              mt: 0.5,
              display: 'flex', 
              justifyContent: 'center',
              alignItems: 'center',
              gap: 1
            }}
          >
            <span>
              {folder.itemCount 
                ? `${folder.itemCount} ${folder.itemCount === 1 ? 'item' : 'items'}`
                : 'Empty folder'}
            </span>
            <Box 
              component="span" 
              sx={{ 
                width: '4px', 
                height: '4px', 
                borderRadius: '50%', 
                backgroundColor: 'text.disabled',
                display: 'inline-block'
              }} 
            />
            <span>{dateFormatted}</span>
          </Typography>
        </CardContent>
      </CardActionArea>

      <Menu
        anchorEl={menuAnchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {isTrash ? (
          <>
            <MenuItem onClick={() => handleMenuItemClick('restore')}>
              <ListItemIcon>
                <RestoreFromTrashIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Restore</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleMenuItemClick('delete')}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText primary="Delete permanently" primaryTypographyProps={{ color: 'error' }} />
            </MenuItem>
          </>
        ) : (
          <>
        <MenuItem onClick={() => handleMenuItemClick('open')}>
          <ListItemIcon>
            <FolderIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Open</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick('rename')}>
          <ListItemIcon>
            <RenameIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Rename</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick('move')}>
          <ListItemIcon>
            <MoveIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Move</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick('copy')}>
          <ListItemIcon>
            <CopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick('share')}>
          <ListItemIcon>
            <ShareIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Share</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick('download')}>
          <ListItemIcon>
            <FolderZipIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download as ZIP</ListItemText>
        </MenuItem>
        <Divider />
        {folder.isStarred ? (
          <MenuItem onClick={() => handleMenuItemClick('star', false)}>
            <ListItemIcon>
              <StarIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Remove from favorites</ListItemText>
          </MenuItem>
        ) : (
          <MenuItem onClick={() => handleMenuItemClick('star', true)}>
            <ListItemIcon>
              <StarBorderIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Add to favorites</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => handleMenuItemClick('properties')}>
          <ListItemIcon>
            <InfoIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Properties</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleMenuItemClick('delete')}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
          </>
        )}
      </Menu>
    </Card>
  );
};

export default FolderCard; 